import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ctsService from '../services/ctsService';
import CerrarCtsConfirmModal from './CerrarCtsConfirmModal';
import { useAuth } from '../hooks/useAuth';
import { obtenerFirmaEmpresaBase64 } from '../services/firmaEmpresaService';
import {
  descargarConstanciaCtsIndividual,
  exportarTodasConstanciasCts,
} from '../utils/ctsConstanciaPdf';
import { calcularResumenCtsDepositoLive, semestreCtsIncluyeMesGratificacion } from '../utils/ctsCalculoCliente';
import { formatFechaSoloDiaDdMmYyyy } from '../utils/formatFechaSoloDia';

const MESES_NOMBRE_POR_NUM = {
  1: 'Enero',
  2: 'Febrero',
  3: 'Marzo',
  4: 'Abril',
  5: 'Mayo',
  6: 'Junio',
  7: 'Julio',
  8: 'Agosto',
  9: 'Septiembre',
  10: 'Octubre',
  11: 'Noviembre',
  12: 'Diciembre',
};

/** YYYY-MM del semestre CTS (mayo = nov–abr; noviembre = may–oct). */
function getOrdenMesesCts(periodo, anio) {
  const y = Number(anio);
  if (!y || Number.isNaN(y)) return [];
  if (periodo === 'mayo') {
    return [`${y - 1}-11`, `${y - 1}-12`, `${y}-01`, `${y}-02`, `${y}-03`, `${y}-04`];
  }
  return [`${y}-05`, `${y}-06`, `${y}-07`, `${y}-08`, `${y}-09`, `${y}-10`];
}

function nombreMesDesdeYm(mesKey) {
  const parts = (mesKey || '').split('-');
  const m = parseInt(parts[1], 10);
  return MESES_NOMBRE_POR_NUM[m] || mesKey;
}

/**
 * Igual que CtsCalculoService::conceptoEsRemuneracionOJornalBasico — identifica remuneración/jornal básico en desglose PLAME.
 */
function conceptoEsRemuneracionOJornalBasicoCts(descripcion) {
  if (!descripcion || typeof descripcion !== 'string') return false;
  const d = descripcion
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase();
  if (!d.includes('BASICO')) return false;
  if (d.includes('REMUNERACION') && d.includes('JORNAL')) return true;
  if (d.includes('JORNAL BASICO')) return true;
  if (d.includes('SUELDO') && d.includes('BASICO')) return true;
  if (d.includes('HABER') && d.includes('BASICO')) return true;
  return false;
}


/** Gratificación bruta que entra al 1/6 (desde rem. computable − base; sin proyección). */
function gratBrutaUsadaParaSexto(row) {
  const b = Number(row?.base_remuneracion);
  const r = Number(row?.remuneracion_computable);
  if (Number.isNaN(b) || Number.isNaN(r) || r < b) return 0;
  return Math.round((r - b) * 6 * 100) / 100;
}

/** Solo meses del semestre CTS desde el primer mes computable por ingreso (clave Y-m). */
function filtrarMesesCtsDesdeIngreso(items, mesDesdeIngresoYm) {
  if (!items?.length) return items || [];
  if (!mesDesdeIngresoYm || typeof mesDesdeIngresoYm !== 'string') return items;
  const pref = mesDesdeIngresoYm.trim();
  const filtrados = items.filter((it) => (it.mes_key || '') >= pref);
  return filtrados.length > 0 ? filtrados : items;
}

function ensureConceptIdsEnFila(row, rowIdx) {
  return {
    ...row,
    pendienteCierre: false,
    conceptosOrigen: row.conceptosOrigen || 'sin_desglose',
    conceptos: (row.conceptos || []).map((c, i) => ({
      ...c,
      id: c.id || `${row.mes_key || rowIdx}-${c.codigo_plame ?? 'c'}-${i}`,
    })),
  };
}

/**
 * Asegura los 6 meses del semestre CTS (mayo: nov–abr; noviembre: may–oct).
 * Los meses no guardados quedan en 0 con concepto editable (manual).
 */
function mergeSueldosConPeriodoCompleto(parsed, periodo, anio) {
  const orden = getOrdenMesesCts(periodo, anio);
  if (!orden.length) return parsed;

  const defaultManual = (mes_key) => ({
    mes: nombreMesDesdeYm(mes_key),
    mes_key,
    monto: 0,
    pendienteCierre: false,
    conceptos: [
      {
        id: `manual-${mes_key}-0`,
        codigo_plame: null,
        nombre: 'Ingreso sin planilla cerrada (indique el monto)',
        monto: 0,
        incluir: true,
      },
    ],
    conceptosOrigen: 'manual',
  });

  if (!parsed.length) {
    return orden.map((mes_key) => defaultManual(mes_key));
  }

  const parsedNorm = parsed.map((s, i) => ensureConceptIdsEnFila(s, i));
  const byKey = Object.fromEntries(parsedNorm.map((s) => [s.mes_key, s]));
  const out = [];
  for (let i = 0; i < orden.length; i += 1) {
    const mes_key = orden[i];
    if (byKey[mes_key]) {
      out.push(ensureConceptIdsEnFila(byKey[mes_key], i));
    } else {
      out.push(defaultManual(mes_key));
    }
  }
  return out;
}

function parseSueldosMensuales(row) {
  const raw = row?.sueldos_mensuales;
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((s, idx) => {
    let origen = s.conceptos_origen || 'sin_desglose';
    if (origen === 'estimado') {
      origen = 'manual';
    }
    return {
      mes: s.mes,
      mes_key: s.mes_key,
      monto: Number(s.monto) || 0,
      pendienteCierre: false,
      conceptos: Array.isArray(s.conceptos)
        ? s.conceptos.map((c, i) => ({
            id: `${s.mes_key || idx}-${c.codigo_plame ?? 'c'}-${i}`,
            codigo_plame: c.codigo_plame ?? null,
            nombre: c.nombre || '',
            monto: Number(c.monto) || 0,
            incluir: c.incluir !== false,
          }))
        : [],
      conceptosOrigen: origen,
    };
  });
}

/** Total conceptos incluidos en CTS de una fila-mes (misma regla que el modal). */
function totalConceptosIncluidosPorItemExport(item) {
  if (!item?.conceptos?.length) return Number(item?.monto) || 0;
  return item.conceptos.filter((c) => c.incluir !== false).reduce((a, c) => a + (Number(c.monto) || 0), 0);
}

/** Los 6 montos del semestre en orden CTS (solo conceptos incluidos por mes). */
function montosSemestreCtsPorFilaExport(detalleRow, periodo, anio) {
  const orden = getOrdenMesesCts(periodo, anio);
  if (!orden.length) return [];
  const merged = mergeSueldosConPeriodoCompleto(parseSueldosMensuales(detalleRow), periodo, anio);
  const porKey = Object.fromEntries(merged.map((m) => [(m.mes_key || '').trim(), m]));
  return orden.map((yk) =>
    porKey[yk] != null ? Math.round(totalConceptosIncluidosPorItemExport(porKey[yk]) * 100) / 100 : 0
  );
}

function etiquetasCabecerasMesesCtsExcel(periodo, anio) {
  return getOrdenMesesCts(periodo, anio).map((yk) => `Rem. ${nombreMesDesdeYm(yk)} (${yk})`);
}

const CtsDetalle = ({ selectedPeriodo, onVolver, onCtsUpdated }) => {
  const { selectedCompany } = useAuth();
  const ctsId = selectedPeriodo?.ctsId ?? selectedPeriodo?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cts, setCts] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [accion, setAccion] = useState(null);
  const [generandoExcel, setGenerandoExcel] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [generandoConstancias, setGenerandoConstancias] = useState(false);
  const [generandoConstanciaIndividual, setGenerandoConstanciaIndividual] = useState(false);
  const [firmaEmpresaBase64, setFirmaEmpresaBase64] = useState(null);
  /** Fila de empleado para modal de desglose (sueldos por mes + conceptos) */
  const [modalFila, setModalFila] = useState(null);
  const [mesIngresosAbierto, setMesIngresosAbierto] = useState(null);
  const [sueldosModalEdit, setSueldosModalEdit] = useState([]);
  const [guardandoDetalle, setGuardandoDetalle] = useState(false);
  const [errorModalDetalle, setErrorModalDetalle] = useState(null);
  /** Gratificación bruta pagada (jul./dic.) para el 1/6; vacío = usar sugerido o estimado */
  const [gratificacionPagadaInput, setGratificacionPagadaInput] = useState('');
  const [modalCierreCtsAbierto, setModalCierreCtsAbierto] = useState(false);

  const cargar = useCallback(async () => {
    if (!ctsId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await ctsService.getById(ctsId);
      const g = res.data || {};
      setCts(g);
      setDetalle(Array.isArray(g.detalle) ? g.detalle : []);
    } catch (err) {
      console.error('Error cargando CTS:', err);
      setError(err.response?.data?.message || 'Error al cargar CTS');
      setCts(null);
      setDetalle([]);
    } finally {
      setLoading(false);
    }
  }, [ctsId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const empresaIdCts = cts?.empresa_id ?? selectedCompany?.id ?? null;

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!empresaIdCts) {
        setFirmaEmpresaBase64(null);
        return;
      }
      const b64 = await obtenerFirmaEmpresaBase64(empresaIdCts);
      if (!cancel) setFirmaEmpresaBase64(b64);
    })();
    return () => {
      cancel = true;
    };
  }, [empresaIdCts]);

  const empresaParaConstancia = useMemo(
    () => ({
      nombre: cts?.empresa_snapshot?.nombre ?? selectedCompany?.name ?? selectedCompany?.nombre_empresa,
      ruc: cts?.empresa_snapshot?.ruc ?? selectedCompany?.ruc,
      direccion: selectedCompany?.direccion ?? selectedCompany?.address ?? '',
    }),
    [cts, selectedCompany]
  );

  const opcionesConstanciaCts = useMemo(() => {
    const rep =
      cts?.empresa_snapshot?.representante_legal ??
      selectedCompany?.representante_legal ??
      '';
    return {
      representanteLegal: rep.trim() || 'Representante Legal',
    };
  }, [cts, selectedCompany]);

  const estaCerrada = cts?.estado === 'cerrada';

  const handleDescargarConstanciaIndividual = async (fila) => {
    if (!fila || !cts) return;
    try {
      setGenerandoConstanciaIndividual(true);
      await descargarConstanciaCtsIndividual({
        empresa: empresaParaConstancia,
        cts,
        detalleRow: fila,
        firmaEmpresaBase64,
        opciones: opcionesConstanciaCts,
      });
    } catch (err) {
      console.error(err);
      alert(err.message || 'No se pudo generar la constancia.');
    } finally {
      setGenerandoConstanciaIndividual(false);
    }
  };

  const handleExportarTodasConstancias = async () => {
    if (!cts || detalle.length === 0) return;
    try {
      setGenerandoConstancias(true);
      const archivos = await exportarTodasConstanciasCts({
        empresa: empresaParaConstancia,
        cts,
        detalle,
        firmaEmpresaBase64,
        opciones: opcionesConstanciaCts,
      });
      const msg =
        archivos.length === 1
          ? `Constancia(s) generada(s): ${archivos[0]}`
          : `Se generaron ${archivos.length} archivos PDF:\n${archivos.join('\n')}`;
      alert(msg);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al generar constancias.');
    } finally {
      setGenerandoConstancias(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return 'S/ 0.00';
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
    }).format(value);
  };

  const getEstadoBadge = (estado) => {
    const estadoConfig = {
      proceso: { class: 'bg-blue-100 text-blue-800', text: 'En Proceso', icon: '⚙️' },
      cerrada: { class: 'bg-green-100 text-green-800', text: 'Cerrada', icon: '✅' },
      calculado: { class: 'bg-yellow-100 text-yellow-800', text: 'Calculado', icon: '🧮' },
      pendiente_calculo: { class: 'bg-orange-100 text-orange-800', text: 'Pendiente de cálculo', icon: '⏳' },
      en_proceso: { class: 'bg-blue-100 text-blue-800', text: 'En Proceso', icon: '⚙️' },
    };
    return estadoConfig[estado] || estadoConfig.calculado;
  };

  const totalConceptosIncluidos = (item) => {
    if (!item?.conceptos?.length) return item?.monto ?? 0;
    return item.conceptos.filter((c) => c.incluir).reduce((a, c) => a + (Number(c.monto) || 0), 0);
  };

  /** Estado visible en tabla: incompleto si falta remuneración mensual obligatoria o gratificación esperada sin fuente automática/manual. */
  const obtenerEstadoVistaFilaCts = (row) => {
    if (!row || estaCerrada || !cts) return row?.estado || 'calculado';
    const periodo = cts.periodo || 'mayo';
    const anio = Number(cts.anio) || new Date().getFullYear();
    const merged = mergeSueldosConPeriodoCompleto(parseSueldosMensuales(row), periodo, anio);
    const visibles = filtrarMesesCtsDesdeIngreso(merged, row.mes_desde_ingreso);
    const faltaMontoMes =
      visibles.length > 0 && visibles.some((mesItem) => totalConceptosIncluidos(mesItem) <= 0);

    const aplicaGrat =
      typeof row.aplica_gratificacion_pagada_cts === 'boolean'
        ? row.aplica_gratificacion_pagada_cts
        : semestreCtsIncluyeMesGratificacion(periodo, anio);
    const sugeridoGrat = Number(row.gratificacion_pagada_sugerida) || 0;
    const grpc = row.gratificacion_pagada_cts;
    const tieneEntradaManualGratificacion =
      grpc !== null && grpc !== undefined && grpc !== '';
    const faltaGratificacionRegistrada =
      aplicaGrat && sugeridoGrat <= 0 && !tieneEntradaManualGratificacion;

    if (faltaMontoMes || faltaGratificacionRegistrada) return 'pendiente_calculo';
    return row.estado || 'calculado';
  };

  /** Meses con desglose PLAME (tabla debajo). Manual va solo en la ficha del mes. */
  const mesRequiereTablaConceptosCts = (item) => {
    if (!item) return false;
    if (item.conceptosOrigen === 'planilla') return true;
    if (item.conceptosOrigen === 'manual') return false;
    return (item.conceptos?.length || 0) > 1;
  };

  /** Monto editable en la ficha del mes (sin tabla). */
  const mesMontoManualSoloEnChipCts = (item) => {
    if (!item || estaCerrada) return false;
    if (item.conceptosOrigen === 'manual') return true;
    return item.conceptosOrigen === 'sin_desglose' && (item.conceptos?.length || 0) <= 1;
  };

  const sueldosMesesVisiblesModal = useMemo(
    () => filtrarMesesCtsDesdeIngreso(sueldosModalEdit, modalFila?.mes_desde_ingreso),
    [sueldosModalEdit, modalFila?.mes_desde_ingreso]
  );

  const resumenCtsModalLive = useMemo(() => {
    if (!modalFila || estaCerrada || !cts) return null;
    const ymdFin = cts.fecha_fin ? String(cts.fecha_fin).slice(0, 10) : null;
    const periodo = cts.periodo || 'mayo';
    const anio = Number(cts.anio) || new Date().getFullYear();
    const aplicaGrat =
      typeof modalFila.aplica_gratificacion_pagada_cts === 'boolean'
        ? modalFila.aplica_gratificacion_pagada_cts
        : semestreCtsIncluyeMesGratificacion(periodo, anio);
    return calcularResumenCtsDepositoLive({
      sueldosMensuales: sueldosModalEdit,
      periodo,
      anio,
      ultimaFechaIngresoLabores: modalFila.ultima_fecha_ingreso_labores,
      fechaFinSemestre: ymdFin,
      regimenCodigo: modalFila.regimen_codigo,
      aplicaGratificacionPagadaCts: aplicaGrat,
      gratificacionPagadaCts:
        (Number(modalFila.gratificacion_pagada_sugerida) || 0) > 0 ? '' : gratificacionPagadaInput,
      gratificacionPagadaSugerida: Number(modalFila.gratificacion_pagada_sugerida) || 0,
    });
  }, [modalFila, estaCerrada, cts, sueldosModalEdit, gratificacionPagadaInput]);

  useEffect(() => {
    if (!modalFila) {
      setMesIngresosAbierto(null);
      setSueldosModalEdit([]);
      setErrorModalDetalle(null);
      return;
    }
    const periodo = cts?.periodo || 'mayo';
    const anio = Number(cts?.anio) || new Date().getFullYear();
    const parsed = parseSueldosMensuales(modalFila);
    const merged = mergeSueldosConPeriodoCompleto(parsed, periodo, anio);
    setSueldosModalEdit(JSON.parse(JSON.stringify(merged)));
    const visibles = filtrarMesesCtsDesdeIngreso(merged, modalFila?.mes_desde_ingreso);
    const primerConTabla = visibles.find((m) => mesRequiereTablaConceptosCts(m));
    setMesIngresosAbierto(primerConTabla?.mes ?? visibles[0]?.mes ?? null);
    setErrorModalDetalle(null);
    const sug = Number(modalFila.gratificacion_pagada_sugerida) || 0;
    const gp = modalFila.gratificacion_pagada_cts;
    if (sug > 0) {
      setGratificacionPagadaInput('');
    } else {
      setGratificacionPagadaInput(gp != null && gp !== '' ? String(Number(gp)) : '');
    }
  }, [modalFila, cts?.periodo, cts?.anio]);

  const toggleConceptoIncluirCts = (mesNombre, conceptoId) => {
    if (estaCerrada) return;
    setSueldosModalEdit((prev) =>
      prev.map((item) => {
        if (item.mes !== mesNombre) return item;
        return {
          ...item,
          conceptos: item.conceptos.map((c) => (c.id === conceptoId ? { ...c, incluir: !c.incluir } : c)),
        };
      })
    );
  };

  const updateConceptoMontoCts = (mesNombre, conceptoId, raw) => {
    if (estaCerrada) return;
    const num = raw === '' ? 0 : Number(raw);
    const value = Number.isNaN(num) ? 0 : Math.round(num * 100) / 100;
    setSueldosModalEdit((prev) =>
      prev.map((item) => {
        if (item.mes !== mesNombre) return item;
        return {
          ...item,
          conceptos: item.conceptos.map((c) => (c.id === conceptoId ? { ...c, monto: value } : c)),
        };
      })
    );
  };

  const buildSueldosPayload = () =>
    sueldosModalEdit.map((item) => ({
      mes: item.mes,
      mes_key: item.mes_key,
      monto: totalConceptosIncluidos(item),
      pendiente_cierre: false,
      conceptos_origen: item.conceptosOrigen || 'sin_desglose',
      conceptos: (item.conceptos || []).map((c) => ({
        codigo_plame: c.codigo_plame ?? null,
        nombre: c.nombre || '',
        monto: Number(c.monto) || 0,
        incluir: !!c.incluir,
      })),
    }));

  const handleGuardarCtsDetalle = async () => {
    if (!ctsId || !modalFila || estaCerrada) return;
    setGuardandoDetalle(true);
    setErrorModalDetalle(null);
    try {
      const payload = { sueldos_mensuales: buildSueldosPayload() };
      const aplicaGrat =
        typeof modalFila.aplica_gratificacion_pagada_cts === 'boolean'
          ? modalFila.aplica_gratificacion_pagada_cts
          : semestreCtsIncluyeMesGratificacion(cts?.periodo || 'mayo', Number(cts?.anio) || new Date().getFullYear());
      if (aplicaGrat) {
        const sug = Number(modalFila.gratificacion_pagada_sugerida) || 0;
        if (sug > 0) {
          payload.gratificacion_pagada_cts = null;
        } else {
          const raw = gratificacionPagadaInput.trim();
          payload.gratificacion_pagada_cts = raw === '' ? null : Number(raw);
        }
      }
      const res = await ctsService.updateDetalle(ctsId, modalFila.id, payload);
      const g = res.data || {};
      setCts(g);
      setDetalle(Array.isArray(g.detalle) ? g.detalle : []);
      if (onCtsUpdated) onCtsUpdated();
      const row = (g.detalle || []).find((d) => d.id === modalFila.id);
      if (row) setModalFila(row);
      alert(res.message || 'Detalle guardado correctamente');
    } catch (err) {
      console.error(err);
      setErrorModalDetalle(err.response?.data?.message || 'Error al guardar');
    } finally {
      setGuardandoDetalle(false);
    }
  };

  const inicialesEmpleado = (nombre) => {
    if (!nombre || typeof nombre !== 'string') return '?';
    const partes = nombre.trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return '?';
    if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
    return `${partes[0].charAt(0)}${partes[partes.length - 1].charAt(0)}`.toUpperCase();
  };

  const periodoLabel = cts?.periodo_nombre || selectedPeriodo?.periodo || '';
  const anioLabel = cts?.anio ?? selectedPeriodo?.año ?? '';

  const handleAbrirModalCierreCts = () => {
    if (!ctsId || estaCerrada) return;
    setModalCierreCtsAbierto(true);
  };

  const handleConfirmarCierreCts = async (entraraPlanilla) => {
    if (!ctsId) return;
    setAccion('cerrar');
    try {
      const res = await ctsService.cerrar(ctsId, entraraPlanilla);
      setModalCierreCtsAbierto(false);
      await cargar();
      if (onCtsUpdated) onCtsUpdated();
      alert(res.message || res.data?.mensaje || 'CTS cerrado correctamente');
    } catch (err) {
      alert(err.response?.data?.message || 'Error al cerrar');
    } finally {
      setAccion(null);
    }
  };

  const handleCancelarModalCierreCts = () => {
    if (accion === 'cerrar') return;
    setModalCierreCtsAbierto(false);
  };

  const handleAbrir = async () => {
    if (!ctsId || !window.confirm('¿Reabrir este CTS?')) return;
    setAccion('abrir');
    try {
      await ctsService.abrir(ctsId);
      await cargar();
      if (onCtsUpdated) onCtsUpdated();
      alert('CTS reabierto correctamente');
    } catch (err) {
      alert(err.response?.data?.message || 'Error al reabrir');
    } finally {
      setAccion(null);
    }
  };

  const exportarCtsExcel = async () => {
    if (detalle.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }
    try {
      setGenerandoExcel(true);
      const titulo = `CTS ${periodoLabel} ${anioLabel}`.trim();
      const periodoExp = cts?.periodo || 'mayo';
      const anioExp = Number(cts?.anio) || new Date().getFullYear();
      const mesCabecerasExcel = etiquetasCabecerasMesesCtsExcel(periodoExp, anioExp);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema Planillas';
      workbook.created = new Date();
      const ws = workbook.addWorksheet('CTS', {
        views: [{ state: 'frozen', ySplit: 4, activeCell: 'A5' }],
        properties: { defaultRowHeight: 20 },
      });
      const headers = [
        'Empleado',
        'Cargo',
        'Régimen',
        'Fecha ingreso',
        '1.er mes cómputo',
        'Días trab.',
        ...mesCabecerasExcel,
        'Promedio 6M',
        'Base rem.',
        'Grat. bruta (1/6)',
        'Rem. computable',
        'Monto CTS',
      ];
      const rowTitulo = ws.getRow(1);
      rowTitulo.getCell(1).value = titulo.toUpperCase();
      ws.mergeCells(1, 1, 1, headers.length);
      rowTitulo.getCell(1).font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      rowTitulo.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
      rowTitulo.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
      rowTitulo.height = 28;
      const rowSub = ws.getRow(2);
      rowSub.getCell(1).value = `Período: ${formatFechaSoloDiaDdMmYyyy(cts?.fecha_inicio, 'N/A')} - ${formatFechaSoloDiaDdMmYyyy(cts?.fecha_fin, 'N/A')}  |  Exportado: ${new Date().toLocaleDateString('es-PE')}`;
      ws.mergeCells(2, 1, 2, headers.length);
      rowSub.getCell(1).font = { size: 10, color: { argb: 'FF6B7280' } };
      rowSub.getCell(1).alignment = { horizontal: 'center' };
      rowSub.height = 20;
      ws.getRow(3).height = 8;
      const rowHeader = ws.getRow(4);
      headers.forEach((h, i) => {
        const cell = rowHeader.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D9488' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      rowHeader.height = 22;

      detalle.forEach((row, idx) => {
        const r = ws.getRow(5 + idx);
        const primerMes =
          row.mes_desde_ingreso && typeof row.mes_desde_ingreso === 'string'
            ? `${nombreMesDesdeYm(row.mes_desde_ingreso)} (${row.mes_desde_ingreso})`
            : '—';
        const mesMontosExcel = montosSemestreCtsPorFilaExport(row, periodoExp, anioExp);
        const vals = [
          row.empleado || '',
          row.cargo || '',
          row.regimen || row.regimen_codigo || '',
          formatFechaSoloDiaDdMmYyyy(row.ultima_fecha_ingreso_labores, '—'),
          primerMes,
          row.diasTrabajados ?? 0,
          ...mesMontosExcel,
          Number(row.promedio6Meses) || 0,
          Number(row.base_remuneracion) || 0,
          Number(gratBrutaUsadaParaSexto(row)),
          Number(row.remuneracion_computable) || 0,
          Number(row.monto_cts) || 0,
        ];
        const idxDias = headers.indexOf('Días trab.');
        vals.forEach((val, i) => {
          const cell = r.getCell(i + 1);
          cell.value = val;
          if (i >= idxDias && idxDias !== -1) {
            cell.numFmt = i === idxDias ? '0' : '#,##0.00';
            cell.alignment = { horizontal: 'right' };
          }
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0FDF4' } };
        });
      });

      const tot = detalle.reduce(
        (acc, row) => ({
          monto: acc.monto + (Number(row.monto_cts) || 0),
        }),
        { monto: 0 }
      );
      const totRow = ws.getRow(5 + detalle.length);
      const ciMonto = headers.indexOf('Monto CTS');
      const colTotalMonto = ciMonto >= 0 ? ciMonto + 1 : headers.length;
      totRow.getCell(1).value = 'TOTALES';
      totRow.getCell(1).font = { bold: true };
      totRow.getCell(colTotalMonto).value = tot.monto;
      totRow.getCell(colTotalMonto).numFmt = '#,##0.00';
      totRow.getCell(colTotalMonto).font = { bold: true };
      totRow.getCell(colTotalMonto).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };

      ws.columns.forEach((col, i) => {
        col.width = Math.min(Math.max((headers[i] || '').length + 2, 12), 36);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CTS_${periodoLabel}_${anioLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      alert('Archivo Excel generado exitosamente');
    } catch (err) {
      console.error('Error exportando Excel:', err);
      alert('Error al generar el Excel: ' + (err.message || ''));
    } finally {
      setGenerandoExcel(false);
    }
  };

  const exportarCtsPDF = async () => {
    if (detalle.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }
    try {
      setGenerandoPDF(true);
      const periodoPdf = cts?.periodo || 'mayo';
      const anioPdf = Number(cts?.anio) || new Date().getFullYear();
      const ordenYmPdf = getOrdenMesesCts(periodoPdf, anioPdf);
      const mesThPdf = ordenYmPdf
        .map(
          (yk) =>
            `<th title="${nombreMesDesdeYm(yk)} (${yk})" style="border:1px solid #d1d5db;padding:4px 2px;font-weight:bold;color:#047857;background:#ecfdf5;font-size:7px;line-height:1.1;"><div>${nombreMesDesdeYm(yk).slice(0, 3)}.</div><div style="font-size:6px;font-weight:normal;">${yk}</div></th>`
        )
        .join('');
      let filasHtml = '';
      let totalCts = 0;
      detalle.forEach((row, idx) => {
        const m = Number(row.monto_cts) || 0;
        totalCts += m;
        const fechaIng = formatFechaSoloDiaDdMmYyyy(row.ultima_fecha_ingreso_labores, '—');
        const mesComp =
          row.mes_desde_ingreso && typeof row.mes_desde_ingreso === 'string'
            ? `${nombreMesDesdeYm(row.mes_desde_ingreso)}`
            : '—';
        const montosPdfRow = montosSemestreCtsPorFilaExport(row, periodoPdf, anioPdf);
        const celMesesPdf = montosPdfRow
          .map(
            (v) =>
              `<td style="border:1px solid #d1d5db;padding:3px;text-align:right;font-size:7px;">${formatCurrency(v)}</td>`
          )
          .join('');
        filasHtml += `
          <tr style="background-color:${idx % 2 === 0 ? '#fff' : '#f9fafb'};">
            <td style="border:1px solid #d1d5db;padding:4px 6px;font-size:8px;color:#111827;">${row.empleado || ''}<br><span style="color:#6b7280;font-size:7px;">${row.cargo || ''} • ${row.regimen || row.regimen_codigo || ''}</span><br><span style="color:#374151;font-size:7px;">Ingreso: ${fechaIng} · Cómputo desde: ${mesComp}</span></td>
            ${celMesesPdf}
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;">${formatCurrency(row.promedio6Meses)}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;">${formatCurrency(row.base_remuneracion)}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;">${formatCurrency(gratBrutaUsadaParaSexto(row))}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;font-weight:bold;color:#047857;">${formatCurrency(row.remuneracion_computable)}</td>
            <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;font-size:8px;font-weight:bold;color:#0d9488;">${formatCurrency(row.monto_cts)}</td>
          </tr>
        `;
      });
      const colspanVaciosPdf = ordenYmPdf.length + 4;
      const contenedor = document.createElement('div');
      contenedor.style.cssText =
        'position:absolute;left:-9999px;top:0;min-width:1400px;width:max-content;max-width:1800px;background:#fff;padding:15px;font-family:Arial,sans-serif;';
      contenedor.innerHTML = `
        <div style="text-align:center;margin-bottom:15px;">
          <h1 style="color:#1f2937;margin-bottom:6px;font-size:16px;font-weight:bold;">Detalle de CTS — ${periodoLabel} ${anioLabel}</h1>
          <p style="color:#6b7280;font-size:10px;">Período: ${formatFechaSoloDiaDdMmYyyy(cts?.fecha_inicio, 'N/A')} - ${formatFechaSoloDiaDdMmYyyy(cts?.fecha_fin, 'N/A')}</p>
          <p style="color:#6b7280;font-size:10px;">Generado: ${new Date().toLocaleDateString('es-PE')}</p>
        </div>
        <table style="width:100%;border-collapse:collapse;table-layout:auto;">
          <thead>
            <tr>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#374151;background:#f9fafb;font-size:8px;min-width:120px;">Empleado</th>
              ${mesThPdf}
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#047857;background:#d1fae5;font-size:8px;">Prom. 6M</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#047857;background:#d1fae5;font-size:8px;">Base</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:#047857;background:#d1fae5;font-size:8px;">Grat. bruta</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:white;background:#047857;font-size:8px;">Rem. comp.</th>
              <th style="border:1px solid #d1d5db;padding:6px;font-weight:bold;color:white;background:#0d9488;font-size:8px;">Monto CTS</th>
            </tr>
          </thead>
          <tbody>${filasHtml}</tbody>
          <tfoot>
            <tr style="background:#f3f4f6;font-weight:bold;">
              <td style="border:1px solid #d1d5db;padding:4px 6px;font-size:8px;">TOTAL CTS</td>
              <td colspan="${colspanVaciosPdf}" style="border:1px solid #d1d5db;"></td>
              <td style="border:1px solid #d1d5db;padding:4px 6px;text-align:right;color:#0d9488;font-size:8px;">${formatCurrency(totalCts)}</td>
            </tr>
          </tfoot>
        </table>
        <p style="margin-top:10px;text-align:center;color:#6b7280;font-size:8px;">Total empleados: ${detalle.length}</p>
      `;
      document.body.appendChild(contenedor);
      const canvas = await html2canvas(contenedor, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: contenedor.scrollWidth,
        height: contenedor.scrollHeight,
        logging: false,
      });
      document.body.removeChild(contenedor);
      const imgData = canvas.toDataURL('image/png', 1.0);
      const canvasW = (canvas.width * 25.4) / 96;
      const canvasH = (canvas.height * 25.4) / 96;
      const pdfW = Math.max(canvasW, 210);
      const pdfH = Math.max(canvasH, 297);
      const pdf = new jsPDF({
        orientation: pdfW > pdfH ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfW, pdfH],
      });
      const scale = Math.min(pdfW / canvasW, pdfH / canvasH, 1);
      pdf.addImage(imgData, 'PNG', (pdfW - canvasW * scale) / 2, (pdfH - canvasH * scale) / 2, canvasW * scale, canvasH * scale);
      pdf.save(`CTS_${periodoLabel}_${anioLabel}_${new Date().toISOString().split('T')[0]}.pdf`);
      alert('PDF generado exitosamente');
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Error al generar el PDF: ' + (err.message || ''));
    } finally {
      setGenerandoPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error && !cts) {
    return (
      <div className="space-y-3 lg:space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
        <button
          type="button"
          onClick={onVolver}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          ← Volver
        </button>
      </div>
    );
  }

  const estadoCabecera = estaCerrada ? 'cerrada' : 'en_proceso';
  const estadoCabeceraConfig = getEstadoBadge(estadoCabecera);

  const totales = detalle.reduce(
    (acc, row) => ({
      promedio: acc.promedio + (Number(row.promedio6Meses) || 0),
      base: acc.base + (Number(row.base_remuneracion) || 0),
      gratBruta: acc.gratBruta + gratBrutaUsadaParaSexto(row),
      remComp: acc.remComp + (Number(row.remuneracion_computable) || 0),
      montoCts: acc.montoCts + (Number(row.monto_cts) || 0),
    }),
    { promedio: 0, base: 0, gratBruta: 0, remComp: 0, montoCts: 0 }
  );

  return (
    <div className="space-y-3 lg:space-y-4">
      {/* Header del detalle — misma estructura que GratificacionesDetalle */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <button
            type="button"
            onClick={onVolver}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors shrink-0"
          >
            ← Volver
          </button>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
              🏦 Detalle de CTS — {periodoLabel} {anioLabel}
            </h1>
            <p className="text-gray-600">
              Período: {formatFechaSoloDiaDdMmYyyy(cts?.fecha_inicio, 'N/A')} al {formatFechaSoloDiaDdMmYyyy(cts?.fecha_fin, 'N/A')}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {cts?.total_empleados ?? detalle.length} empleados
              </span>
              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                Total CTS: {formatCurrency(cts?.total_calculado ?? totales.montoCts)}
              </span>
              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoCabeceraConfig.class}`}>
                <span className="mr-1">{estadoCabeceraConfig.icon}</span>
                {estadoCabeceraConfig.text}
              </span>
              {estaCerrada && cts?.entrara_en_planilla !== undefined && cts?.entrara_en_planilla !== null ? (
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                    cts.entrara_en_planilla ? 'bg-teal-100 text-teal-900' : 'bg-gray-100 text-gray-700'
                  }`}
                  title="Declaración al momento de cerrar el CTS respecto del ingreso a planilla."
                >
                  {cts.entrara_en_planilla ? 'Declarado: ingreso a planilla previsto' : 'Declarado: sin ingreso a planilla desde este cierre'}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {!estaCerrada && (
            <button
              type="button"
              onClick={handleAbrirModalCierreCts}
              disabled={!!accion}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {accion === 'cerrar' ? 'Cerrando…' : 'Cerrar CTS'}
            </button>
          )}
          {estaCerrada && (
            <button
              type="button"
              onClick={handleAbrir}
              disabled={!!accion}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
            >
              {accion === 'abrir' ? 'Abriendo…' : 'Reabrir CTS'}
            </button>
          )}
          <button
            type="button"
            onClick={exportarCtsExcel}
            disabled={generandoExcel || detalle.length === 0}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Exportar a Excel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{generandoExcel ? 'Generando...' : 'Excel'}</span>
          </button>
          <button
            type="button"
            onClick={handleExportarTodasConstancias}
            disabled={generandoConstancias || detalle.length === 0}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Constancias de depósito CTS (con firma de empresa si está configurada)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>{generandoConstancias ? 'Generando…' : 'Constancias PDF'}</span>
          </button>
          <button
            type="button"
            onClick={exportarCtsPDF}
            disabled={generandoPDF || detalle.length === 0}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Resumen del CTS en PDF (tabla)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>{generandoPDF ? 'Generando...' : 'PDF'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm rounded-t-xl">{error}</div>
        )}
        {detalle.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay empleados</h3>
            <p className="mt-1 text-sm text-gray-500">No hay líneas de detalle para este CTS.</p>
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empleado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Régimen</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingreso</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Días trab.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Promedio 6M</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base rem.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grat. bruta (1/6)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rem. computable</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto CTS</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {detalle.map((row) => {
                    const estadoConfig = getEstadoBadge(obtenerEstadoVistaFilaCts(row));
                    return (
                      <tr key={`${row.id}-${row.empleado}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">{inicialesEmpleado(row.empleado)}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{row.empleado}</div>
                              <div className="text-xs text-gray-500">{row.cargo || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {row.regimen || row.regimen_codigo || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">{formatFechaSoloDiaDdMmYyyy(row.ultima_fecha_ingreso_labores, '—')}</div>
                          {row.mes_desde_ingreso ? (
                            <div className="text-xs text-gray-500 mt-0.5">
                              Cómputo desde {nombreMesDesdeYm(row.mes_desde_ingreso)}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.diasTrabajados ?? '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(row.promedio6Meses)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.base_remuneracion)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(gratBrutaUsadaParaSexto(row))}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{formatCurrency(row.remuneracion_computable)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">{formatCurrency(row.monto_cts)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                            <span className="mr-1">{estadoConfig.icon}</span>
                            {estadoConfig.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            type="button"
                            onClick={() => {
                              setModalFila(row);
                              setMesIngresosAbierto(null);
                            }}
                            className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                            title="Ver desglose por mes y conceptos"
                          >
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                    <td colSpan={2} className="px-6 py-4 text-sm text-gray-900">
                      Total
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">—</td>
                    <td className="px-6 py-4 text-sm text-gray-500">—</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(totales.promedio)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(totales.base)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(totales.gratBruta)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-green-600">{formatCurrency(totales.remComp)}</td>
                    <td className="px-6 py-4 text-sm font-bold text-blue-600">{formatCurrency(totales.montoCts)}</td>
                    <td colSpan={2} className="px-6 py-4" />
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="lg:hidden">
              <div className="divide-y divide-gray-200">
                {detalle.map((row) => {
                  const estadoConfig = getEstadoBadge(obtenerEstadoVistaFilaCts(row));
                  return (
                    <div key={`${row.id}-${row.empleado}`} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-white font-bold text-sm">{inicialesEmpleado(row.empleado)}</span>
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{row.empleado}</div>
                            <div className="text-xs text-gray-500 truncate">{row.cargo || '—'}</div>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full shrink-0 ml-2 ${estadoConfig.class}`}>
                          <span className="mr-1">{estadoConfig.icon}</span>
                          {estadoConfig.text}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Régimen:</span>
                          <div className="font-medium text-gray-900">{row.regimen || row.regimen_codigo || '—'}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Ingreso:</span>
                          <div className="font-medium text-gray-900">{formatFechaSoloDiaDdMmYyyy(row.ultima_fecha_ingreso_labores, '—')}</div>
                          {row.mes_desde_ingreso ? (
                            <div className="text-xs text-gray-500 mt-0.5">
                              Cómputo desde {nombreMesDesdeYm(row.mes_desde_ingreso)}
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <span className="text-gray-500">Días trab.:</span>
                          <div className="font-medium text-gray-900">{row.diasTrabajados ?? '—'}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Promedio 6M:</span>
                          <div className="font-medium text-gray-900">{formatCurrency(row.promedio6Meses)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Base rem.:</span>
                          <div className="font-medium text-gray-900">{formatCurrency(row.base_remuneracion)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Rem. computable:</span>
                          <div className="font-medium text-green-600">{formatCurrency(row.remuneracion_computable)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Monto CTS:</span>
                          <div className="font-bold text-blue-600">{formatCurrency(row.monto_cts)}</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => {
                            setModalFila(row);
                            setMesIngresosAbierto(null);
                          }}
                          className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                        >
                          Ver detalle
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-0 p-4 bg-gray-50 rounded-b-xl border-t-2 border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Total</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Rem. computable:</span>
                    <div className="font-medium text-green-600">{formatCurrency(totales.remComp)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Monto CTS:</span>
                    <div className="font-bold text-blue-600">{formatCurrency(totales.montoCts)}</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {modalFila && (() => {
        const resumenModalBase = estaCerrada ? modalFila : { ...modalFila, ...(resumenCtsModalLive || {}) };
        const baseR = Number(resumenModalBase.base_remuneracion);
        const remC = Number(resumenModalBase.remuneracion_computable);
        const gratParaDerivado =
          resumenModalBase.gratificacion_para_sexto != null && !Number.isNaN(Number(resumenModalBase.gratificacion_para_sexto))
            ? Number(resumenModalBase.gratificacion_para_sexto)
            : !Number.isNaN(baseR) && !Number.isNaN(remC)
              ? Math.round((remC - baseR) * 6 * 100) / 100
              : null;
        const aporteDerivado =
          resumenModalBase.aporte_sexto_gratificacion != null &&
          !Number.isNaN(Number(resumenModalBase.aporte_sexto_gratificacion))
            ? Number(resumenModalBase.aporte_sexto_gratificacion)
            : gratParaDerivado != null && !Number.isNaN(gratParaDerivado)
              ? Math.round((gratParaDerivado / 6) * 100) / 100
              : null;
        const resumenModal = {
          ...resumenModalBase,
          gratificacion_para_sexto: gratParaDerivado,
          aporte_sexto_gratificacion: aporteDerivado,
        };
        const aplicaGratifPagada =
          typeof modalFila.aplica_gratificacion_pagada_cts === 'boolean'
            ? modalFila.aplica_gratificacion_pagada_cts
            : semestreCtsIncluyeMesGratificacion(cts?.periodo || 'mayo', Number(cts?.anio) || new Date().getFullYear());
        const sugeridoGrat = Number(modalFila.gratificacion_pagada_sugerida) || 0;
        const tieneGratifRegistrada = sugeridoGrat > 0;
        const esFija = (resumenModal.tipo_remuneracion || '').toLowerCase() === 'fija';

        const remCValForm = Number(resumenModal.remuneracion_computable) || 0;
        const baseValForm = Number(resumenModal.base_remuneracion) || 0;
        const gratBrutaForm =
          resumenModal.gratificacion_para_sexto != null && !Number.isNaN(Number(resumenModal.gratificacion_para_sexto))
            ? Number(resumenModal.gratificacion_para_sexto)
            : 0;
        const montoValForm = Number(resumenModal.monto_cts) || 0;
        const mesEqForm =
          typeof resumenModal.meses_equivalentes_cts === 'number' && !Number.isNaN(resumenModal.meses_equivalentes_cts)
            ? resumenModal.meses_equivalentes_cts
            : null;
        const mesEqStr =
          mesEqForm != null
            ? mesEqForm.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
            : '—';
        const esMyppPequenia = String(modalFila.regimen_codigo || '').trim().toUpperCase() === 'MYPP';
        const sufijoMitadMypp = esMyppPequenia ? ' × 0,5 (MYPE Pequeña Empresa)' : '';
        const formulaRemComputable = aplicaGratifPagada
          ? `Base + (Grat. bruta ÷ 6) = ${formatCurrency(baseValForm)} + (${formatCurrency(gratBrutaForm)} ÷ 6) = ${formatCurrency(
              remCValForm
            )}`
          : `Remuneración computable (base del período) = ${formatCurrency(remCValForm)}`;

        const tieneDesgloseTiempoMd =
          mesEqForm != null &&
          !Number.isNaN(mesEqForm) &&
          typeof resumenModal.meses_completos_cts === 'number' &&
          !Number.isNaN(Number(resumenModal.meses_completos_cts)) &&
          typeof resumenModal.dias_fraccion_cts === 'number' &&
          !Number.isNaN(Number(resumenModal.dias_fraccion_cts));

        let formulaMontoCtsDeposito = null;
        if (mesEqForm != null && !Number.isNaN(mesEqForm)) {
          if (!tieneDesgloseTiempoMd) {
            formulaMontoCtsDeposito = `(Rem. computable ÷ 12) × meses equiv. (${mesEqStr})${sufijoMitadMypp} = (${formatCurrency(
              remCValForm
            )} ÷ 12) × ${mesEqStr}${esMyppPequenia ? ' × 0,5' : ''} = ${formatCurrency(montoValForm)}`;
          }
        }

        /* Desglose por meses completos / días (cuando viene del backend): */
        const mcCts = tieneDesgloseTiempoMd ? Math.max(0, Number(resumenModal.meses_completos_cts) || 0) : 0;
        const diasCts = tieneDesgloseTiempoMd ? Math.max(0, Number(resumenModal.dias_fraccion_cts) || 0) : 0;
        const TOPE_MESES_CTSDEPOSITO = 6;
        const cumpleTopeMesesCompletosCts = mcCts >= TOPE_MESES_CTSDEPOSITO;
        /** Con 6 meses completos el depósito ya alcanzó el máximo legal: no mostramos fracción en días */
        const mostrarApartadoFraccionDias = diasCts > 0 && !cumpleTopeMesesCompletosCts;
        const round2fn = (x) => Math.round(Number(x) * 100) / 100;
        const ctsMensualDesdeRc = round2fn(remCValForm / 12);
        const partePorMeses = round2fn(ctsMensualDesdeRc * mcCts);
        const partePorDias = diasCts > 0 ? round2fn(ctsMensualDesdeRc * (diasCts / 30)) : 0;
        /* Subtotal “tiempo” que usa el servidor: (Rem÷12)×(mesEquiv), un solo redondeo */
        const subtotalTiempoSinMypp =
          mesEqForm != null ? round2fn(ctsMensualDesdeRc * mesEqForm) : round2fn(partePorMeses + partePorDias);
        const sumaDosBloques = round2fn(partePorMeses + partePorDias);
        const hayDiscrepanciaSumaVsOficial =
          tieneDesgloseTiempoMd &&
          mostrarApartadoFraccionDias &&
          diasCts > 0 &&
          mcCts > 0 &&
          Math.abs(sumaDosBloques - subtotalTiempoSinMypp) >= 0.02;

        return (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="cts-modal-title" role="dialog" aria-modal="true">
            <div className="flex min-h-screen items-center justify-center p-4">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !guardandoDetalle && setModalFila(null)}
                aria-hidden="true"
              />
              <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-4">
                    <h3 className="text-lg font-bold text-gray-900" id="cts-modal-title">
                      Ver detalle — {modalFila.empleado}
                    </h3>
                    <button
                      type="button"
                      onClick={() => !guardandoDetalle && setModalFila(null)}
                      className="text-gray-400 hover:text-gray-600 rounded-lg p-1"
                      aria-label="Cerrar"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">{modalFila.cargo || '—'}</p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {modalFila.regimen || modalFila.regimen_codigo || '—'}
                    </span>
                  </p>
                  <p className="text-xs text-gray-600 mb-4">
                    <span className="font-medium text-gray-700">Ingreso a labores:</span>{' '}
                    {formatFechaSoloDiaDdMmYyyy(modalFila.ultima_fecha_ingreso_labores, '—')}
                    {modalFila.mes_desde_ingreso ? (
                      <>
                        {' '}
                        · <span className="font-medium text-gray-700">Primer mes en semestre CTS:</span>{' '}
                        {nombreMesDesdeYm(modalFila.mes_desde_ingreso)} ({modalFila.mes_desde_ingreso})
                      </>
                    ) : null}
                  </p>

                  <div
                    className={`rounded-lg p-4 mb-4 border ${
                      esFija ? 'bg-emerald-50 border-emerald-200' : 'bg-sky-50 border-sky-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Remuneración:{' '}
                      <span className={esFija ? 'text-emerald-700 font-semibold' : 'text-sky-700 font-semibold'}>
                        {esFija ? 'Fija' : 'Variable'}
                      </span>
                    </p>
                    <p className="text-sm text-gray-800">
                      Base de remuneración (período CTS):{' '}
                      <strong>{formatCurrency(resumenModal.base_remuneracion)}</strong>
                    </p>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Ingresos por mes (planillas cerradas)</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      {estaCerrada
                        ? modalFila?.mes_desde_ingreso
                          ? `Solo se listan los meses desde ${nombreMesDesdeYm(modalFila.mes_desde_ingreso)}. Desglose PLAME al cierre (solo lectura).`
                          : 'Desglose por concepto PLAME al momento del cálculo (solo lectura).'
                        : modalFila?.mes_desde_ingreso
                          ? `Solo se muestran los meses del semestre desde el ingreso (${nombreMesDesdeYm(modalFila.mes_desde_ingreso)} en adelante). El guardado sigue enviando el semestre completo al servidor. Por defecto solo se incluye en el total remuneración o jornal básico; marque otros conceptos si debe computarse. Gratificación (PLAME 0406 o 0313) y bonificaciones extraordinarias Ley no aparecen en esta tabla: el sugerido se arma desde la planilla o módulo gratificaciones.`
                          : 'Se muestran los 6 meses del semestre (CTS Mayo: nov.–abr.; CTS Nov.: may.–oct.). En meses sin planilla, indique el monto en la ficha del mes y pulse Guardar. Use la tabla solo cuando hay conceptos PLAME por planilla cerrada. Por defecto solo se incluye remuneración o jornal básico; marque otros conceptos si debe computarse. Gratificación PLAME (0406/0313) y bonificaciones extraordinarias Ley no se listan aquí: el sistema las usa sólo para el apartado Gratificación bruta pagada (sugerido).'}
                    </p>
                    {sueldosModalEdit.length === 0 ? (
                      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        No se pudo armar el calendario de meses (período o año del CTS no disponible). Recargue la página o
                        contacte al administrador.
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {sueldosMesesVisiblesModal.map((item) => {
                            const total = totalConceptosIncluidos(item);
                            const requiereTabla = mesRequiereTablaConceptosCts(item);
                            const montoSoloEnChip = mesMontoManualSoloEnChipCts(item);
                            const activo = mesIngresosAbierto === item.mes && requiereTabla;
                            const chipBase = `inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              activo
                                ? 'bg-green-100 border-green-400 text-green-900 ring-1 ring-green-400'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`;
                            const primeraLinea = item.conceptos?.[0];
                            const contenidoMes = (
                              <>
                                <span className="font-medium">{item.mes}</span>
                                {montoSoloEnChip && primeraLinea ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    disabled={estaCerrada}
                                    value={Number.isFinite(Number(primeraLinea.monto)) ? Number(primeraLinea.monto) : 0}
                                    onChange={(e) =>
                                      updateConceptoMontoCts(item.mes, primeraLinea.id, e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-24 px-2 py-0.5 text-right border border-gray-300 rounded text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                                    title="Monto del mes (sin planilla cerrada)"
                                  />
                                ) : (
                                  <span className="font-semibold">{formatCurrency(total)}</span>
                                )}
                              </>
                            );

                            if (requiereTabla) {
                              return (
                                <button
                                  key={item.mes_key || item.mes}
                                  type="button"
                                  onClick={() => setMesIngresosAbierto((m) => (m === item.mes ? null : item.mes))}
                                  className={chipBase}
                                >
                                  {contenidoMes}
                                </button>
                              );
                            }

                            return (
                              <div
                                key={item.mes_key || item.mes}
                                className={`${chipBase} cursor-default`}
                                role="group"
                                aria-label={`Mes ${item.mes}`}
                              >
                                {contenidoMes}
                              </div>
                            );
                          })}
                        </div>
                        {mesIngresosAbierto && (() => {
                          const item = sueldosModalEdit.find((i) => i.mes === mesIngresosAbierto);
                          if (!item || !mesRequiereTablaConceptosCts(item)) return null;
                          return (
                            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
                              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-800">
                                Ingresos de {item.mes}
                                {item.conceptosOrigen === 'planilla' && (
                                  <span className="ml-2 text-xs font-normal text-gray-600">
                                    (conceptos PLAME — planilla cerrada
                                    {modalFila?.mes_desde_ingreso && item.mes_key === modalFila.mes_desde_ingreso
                                      ? '; en este mes puede ajustar el monto de remuneración o jornal básico'
                                      : ''}
                                    )
                                  </span>
                                )}
                                {item.conceptosOrigen === 'sin_desglose' && (
                                  <span className="ml-2 text-xs font-normal text-gray-500">(sin desglose en planilla)</span>
                                )}
                                {item.conceptosOrigen === 'manual' && (
                                  <span className="ml-2 text-xs font-normal text-amber-800">
                                    (sin planilla cerrada — monto manual)
                                  </span>
                                )}
                              </div>
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">
                                      Incluir
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                      Concepto
                                    </th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                      Monto
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {item.conceptos.map((c) => {
                                    const esPrimerMesComputoDelSemestre =
                                      !!(modalFila?.mes_desde_ingreso && item.mes_key === modalFila.mes_desde_ingreso);
                                    const permiteEdicionBasicoPlanilla =
                                      esPrimerMesComputoDelSemestre &&
                                      item.conceptosOrigen === 'planilla' &&
                                      conceptoEsRemuneracionOJornalBasicoCts(c.nombre);
                                    const montoEditable =
                                      !estaCerrada &&
                                      (item.conceptosOrigen === 'manual' ||
                                        item.conceptosOrigen === 'sin_desglose' ||
                                        permiteEdicionBasicoPlanilla);
                                    return (
                                      <tr key={c.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                          <input
                                            type="checkbox"
                                            checked={!!c.incluir}
                                            disabled={estaCerrada}
                                            onChange={() => toggleConceptoIncluirCts(item.mes, c.id)}
                                            className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                                            title={estaCerrada ? 'CTS cerrado' : 'Incluir en el total del mes'}
                                          />
                                        </td>
                                        <td className="px-4 py-2 text-gray-900">{c.nombre}</td>
                                        <td className="px-4 py-2 text-right font-medium text-gray-900">
                                          {montoEditable ? (
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              disabled={estaCerrada}
                                              value={Number.isFinite(Number(c.monto)) ? Number(c.monto) : 0}
                                              onChange={(e) => updateConceptoMontoCts(item.mes, c.id, e.target.value)}
                                              className="w-32 ml-auto block rounded-md border border-gray-300 px-2 py-1 text-sm text-right text-gray-900 focus:border-green-500 focus:ring-green-500 disabled:bg-gray-100"
                                              title={
                                                permiteEdicionBasicoPlanilla
                                                  ? 'Primer mes de cómputo en el semestre: puede corregir el jornal básico efectivo respecto del mes incompleto en planilla'
                                                  : 'Monto del concepto para este mes'
                                              }
                                            />
                                          ) : (
                                            formatCurrency(c.monto)
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 text-sm text-right">
                                <span className="text-gray-600">Total considerado para el mes: </span>
                                <span className="font-semibold text-gray-900">{formatCurrency(totalConceptosIncluidos(item))}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumen CTS</h4>
                    {!estaCerrada && resumenCtsModalLive && (
                      <p className="text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 mb-3">
                        Vista previa al vuelo (misma regla que liquidación):{' '}
                        <strong>remuneración computable ÷ 12 × (meses completos + días/30)</strong>, tope 6. La
                        remuneración computable es <strong>base + (gratificación bruta para el sexto ÷ 6)</strong>.
                        {modalFila.regimen_codigo === 'MYPP' ? (
                          <>
                            {' '}
                            Este trabajador es <strong>MYPE Pequeña Empresa (MYPP)</strong>: el monto CTS se aplica{' '}
                            <strong>× 1/2</strong> sobre el resultado indicado (igual que en liquidaciones).
                          </>
                        ) : null}
                        {aplicaGratifPagada ? (
                          <>
                            {' '}
                            Si el semestre incluye <strong>julio o diciembre</strong>, para el 1/6 sobre la gratificación{' '}
                            {tieneGratifRegistrada ? (
                              <>
                                se usa el importe registrado (<strong>sugerido</strong>{' '}
                                desde planilla 0406/0313 o módulo gratificaciones). No debe completar ningún campo manual aquí.
                              </>
                            ) : (
                              <>
                                puede especificar gratificación bruta opcionalmente; si no indica ningún monto manual, esos
                                importes pueden detectarse igualmente en planilla o en gratificaciones. Sin sugerido ni
                                ingreso manual, el 1/6 queda en cero (no se proyecta gratificación).
                              </>
                            )}
                            {modalFila.regimen_codigo === 'MYPP' ? (
                              <> En MYPP, el sugerido automático puede traer gratificación bruta ya a la mitad.</>
                            ) : null}
                          </>
                        ) : null}{' '}
                        Al guardar, el servidor persiste con la misma fórmula.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mb-3">
                      {estaCerrada ? '' : 'Los días trabajados del semestre y el desglose por mes están arriba; aquí sólo figura cómo el sistema arma los montos con la misma regla legal.'}
                    </p>
                    {aplicaGratifPagada ? (
                      <div className="mb-4 rounded-md border border-amber-100 bg-amber-50/90 px-3 py-2">
                        <div className="text-gray-700 font-medium text-sm mb-1">Gratificación bruta pagada (para el 1/6)</div>
                        <div className="space-y-2">
                          <p className="text-xs text-gray-600">
                            Sugerido (planilla cerrada o módulo gratificaciones):{' '}
                            <span className="font-semibold text-gray-800">{formatCurrency(sugeridoGrat)}</span>
                            .
                            {tieneGratifRegistrada ? (
                              <> Este importe viene del sistema: se usará para el 1/6 sin ingreso manual.</>
                            ) : (
                              <>
                                {' '}
                                Si no aparece valor en planilla/gratificación, puede indicar la gratificación bruta en el campo
                                inferior; déjelo vacío para quedar sin 1/6 por gratificación.
                              </>
                            )}
                          </p>
                          {modalFila.regimen_codigo === 'MYPP' ? (
                            <p className="text-xs text-amber-900">
                              MYPP (MYPE Pequeña Empresa): el sugerido y el cálculo automático aplican la gratificación bruta
                              a la mitad.
                              {!tieneGratifRegistrada ? (
                                <>
                                  Si indica un monto manual, debe ser la gratificación bruta efectivamente pagada (sin dividir
                                  otra vez).
                                </>
                              ) : null}
                            </p>
                          ) : null}
                          {!estaCerrada ? (
                            <>
                              {!tieneGratifRegistrada ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    disabled={estaCerrada}
                                    placeholder="Opcional — gratificación bruta"
                                    value={gratificacionPagadaInput}
                                    onChange={(e) => setGratificacionPagadaInput(e.target.value)}
                                    className="w-52 rounded-md border border-amber-200 bg-white px-2 py-1.5 text-sm text-right text-gray-900 focus:border-green-500 focus:ring-green-500 disabled:bg-gray-100"
                                    title="Solo cuando no hay sugerido de planilla o módulo gratificaciones"
                                  />
                                  <button
                                    type="button"
                                    disabled={estaCerrada}
                                    onClick={() => setGratificacionPagadaInput('')}
                                    className="text-xs font-medium text-amber-900 underline-offset-2 hover:underline disabled:opacity-50"
                                  >
                                    Dejar en blanco (sin gratificación registrada ni manual)
                                  </button>
                                </div>
                              ) : (
                                <p className="text-xs text-green-900 font-medium">
                                  Gratificación tomada automáticamente del sugerido; no tiene campo opcional porque el sistema
                                  ya la registró en planillas o gratificaciones.
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm font-medium text-gray-900">
                              {modalFila.gratificacion_pagada_cts != null
                                ? formatCurrency(modalFila.gratificacion_pagada_cts)
                                : tieneGratifRegistrada
                                  ? `${formatCurrency(sugeridoGrat)} — según sistema (planilla/gratificación)`
                                  : 'Sin valor guardado'}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-lg border border-green-200 bg-green-50/90 p-3">
                      <h5 className="text-xs font-semibold text-emerald-900 uppercase tracking-wide mb-2">
                        Fórmula aplicada
                      </h5>
                      <ul className="text-sm text-green-800 space-y-1.5">
                        <li className="pl-2 border-l-2 border-green-400">
                          <span className="font-medium text-gray-900">Remuneración computable</span>
                          <div className="mt-0.5">{formulaRemComputable}</div>
                        </li>
                        <li className="pl-2 border-l-2 border-green-400">
                          <span className="font-medium text-gray-900">CTS del depósito (semestre)</span>
                          {!mesEqForm || Number.isNaN(mesEqForm) ? (
                            <div className="mt-0.5 text-gray-600">
                              No hay meses equivalentes para armar la expresión (revise fechas de ingreso y fin de semestre).
                            </div>
                          ) : formulaMontoCtsDeposito ? (
                            <div className="mt-0.5">{formulaMontoCtsDeposito}</div>
                          ) : tieneDesgloseTiempoMd ? (
                            <div className="mt-0.5 space-y-2">
                              <div className="text-xs text-gray-600 leading-relaxed">
                                <strong>Tiempo computable CTS</strong>: días naturales desde el ingreso hasta el fin del semestre
                                (inclusive), agrupados en bloques de 30 días (treintena); la fracción es el resto ÷ 30; tope
                                legal 6 meses equivalentes. Misma lógica que el servidor al guardar. En este caso:{' '}
                                <strong>{mcCts}</strong> bloque{mcCts === 1 ? '' : 's'} de 30 días
                                {mostrarApartadoFraccionDias ? (
                                  <>
                                    {' '}
                                    y <strong>{diasCts}</strong> día{diasCts === 1 ? '' : 's'} de fracción (/30).
                                  </>
                                ) : cumpleTopeMesesCompletosCts ? (
                                  <> — tope legal alcanzado (no suma fracción en días).</>
                                ) : (
                                  <> (sin fracción en días).</>
                                )}
                              </div>
                              <p className="text-[11px] text-gray-500 leading-snug">
                                No se usan «meses calendario rodantes» desde el día de ingreso: el desglose en bloques de 30 y
                                días resto refleja la treintena del período. La tabla de ingresos puede arrancar en un mes
                                distinto del calendario porque marca el primer mes en que ya hubo vínculo al cierre mensual.
                              </p>
                              <div>
                                <span className="font-medium text-gray-800">CTS mensual (base CTS): </span>
                                {formatCurrency(remCValForm)} ÷ 12 = <strong>{formatCurrency(ctsMensualDesdeRc)}</strong>
                              </div>

                              <div className="rounded-md border border-green-200 bg-white/60 px-2.5 py-2 space-y-1.5">
                                <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                                  Treintenas (30 días)
                                </div>
                                {mcCts > 0 ? (
                                  <div className="text-sm text-green-900">
                                    {formatCurrency(ctsMensualDesdeRc)} × {mcCts} = <strong>{formatCurrency(partePorMeses)}</strong>
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-600">Sin bloques completos de 30 días en el cómputo.</div>
                                )}
                              </div>

                              {mcCts < TOPE_MESES_CTSDEPOSITO ? (
                                <div className="rounded-md border border-green-200 bg-white/60 px-2.5 py-2 space-y-1.5">
                                  <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                                    Fracción en días (/30)
                                  </div>
                                  {diasCts > 0 ? (
                                    <div className="text-sm text-green-900">
                                      {formatCurrency(ctsMensualDesdeRc)} × ({diasCts} ÷ 30) ={' '}
                                      <strong>{formatCurrency(partePorDias)}</strong>
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-600">Sin fracción de mes en días.</div>
                                  )}
                                </div>
                              ) : null}

                              <div className="text-sm text-green-900">
                                <span className="font-medium text-gray-800">CTS por tiempo: </span>
                                {estaCerrada ? (
                                  <strong>{formatCurrency(montoValForm)}</strong>
                                ) : mcCts > 0 && mostrarApartadoFraccionDias ? (
                                  <>
                                    {formatCurrency(partePorMeses)} + {formatCurrency(partePorDias)} ={' '}
                                    <strong>{formatCurrency(subtotalTiempoSinMypp)}</strong>
                                  </>
                                ) : mcCts > 0 ? (
                                  <strong>{formatCurrency(subtotalTiempoSinMypp)}</strong>
                                ) : diasCts > 0 ? (
                                  <strong>{formatCurrency(subtotalTiempoSinMypp)}</strong>
                                ) : (
                                  <>—</>
                                )}
                              </div>
                              {hayDiscrepanciaSumaVsOficial && !estaCerrada ? (
                                <p className="text-xs text-gray-500 italic">
                                  Nota: la suma de meses completos más días (/30), con redondeos por apartado, puede diferir por
                                  céntimos frente al cálculo unificado que usa el sistema;{' '}
                                  <strong className="not-italic text-gray-700">
                                    prevalece {formatCurrency(subtotalTiempoSinMypp)}
                                  </strong>
                                  .
                                </p>
                              ) : null}
                              {esMyppPequenia ? (
                                <div className="pt-1 border-t border-green-200/70">
                                  Régimen <strong>MYPE Pequeña (MYPP)</strong>: aplicar Ley MYPE sobre el CTS de depósito:{' '}
                                  {formatCurrency(subtotalTiempoSinMypp)} × 0,5 ={' '}
                                  <strong>{formatCurrency(montoValForm)}</strong>.
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 border-t border-gray-200 pt-4">
                      {errorModalDetalle && (
                        <p className="text-sm text-red-600 sm:mr-auto order-2 sm:order-1">{errorModalDetalle}</p>
                      )}
                      <div className="flex flex-wrap justify-end gap-2 order-1 sm:order-2">
                        <button
                          type="button"
                          onClick={() => handleDescargarConstanciaIndividual(modalFila)}
                          disabled={generandoConstanciaIndividual || !modalFila}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium disabled:opacity-50"
                          title="Constancia de depósito semestral con firma de gerencia"
                        >
                          {generandoConstanciaIndividual ? 'Generando…' : 'Constancia PDF'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalFila(null)}
                          disabled={guardandoDetalle}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50"
                        >
                          Cerrar ventana
                        </button>
                        {!estaCerrada && sueldosModalEdit.length > 0 ? (
                          <button
                            type="button"
                            onClick={handleGuardarCtsDetalle}
                            disabled={guardandoDetalle}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                          >
                            {guardandoDetalle ? 'Guardando…' : 'Guardar cambios'}
                          </button>
                        ) : null}
                      </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <CerrarCtsConfirmModal
        open={modalCierreCtsAbierto && !estaCerrada}
        subtitulo={cts?.nombre || `${periodoLabel} ${anioLabel}`.trim() || null}
        submitting={accion === 'cerrar'}
        onCancel={handleCancelarModalCierreCts}
        onConfirm={handleConfirmarCierreCts}
      />
    </div>
  );
};

export default CtsDetalle;
